import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Layout from '../components/Layout';

// ─── constants ───────────────────────────────────────────────────────────────

const TYPE_META = {
  video:     { label: 'Video',     color: '#003479', bg: 'rgba(0,52,121,0.10)',   icon: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664zM21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
  article:   { label: 'Article',   color: '#20C997', bg: 'rgba(32,201,151,0.10)', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
  job_alert: { label: 'Job Alert', color: '#f59e0b', bg: 'rgba(245,158,11,0.10)', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  tip:       { label: 'Tip',       color: '#8b5cf6', bg: 'rgba(139,92,246,0.10)', icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z' },
};

const TYPES = Object.keys(TYPE_META);

// ─── helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const Toast = ({ msg, onClose }) => (
  <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-gray-900 text-white px-5 py-3 rounded-2xl shadow-xl animate-fade-in">
    <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    <span className="text-sm font-medium">{msg}</span>
    <button onClick={onClose} className="ml-2 text-gray-400 hover:text-white">
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
);

const useToast = () => {
  const [toast, setToast] = useState('');
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };
  return { toast, showToast };
};

const TypeBadge = ({ type }) => {
  const meta = TYPE_META[type] || TYPE_META.tip;
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold"
      style={{ color: meta.color, background: meta.bg }}
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={meta.icon} />
      </svg>
      {meta.label}
    </span>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const TrainerFeed = () => {
  const { toast, showToast } = useToast();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    type: 'video',
    title: '',
    content: '',
    link: '',
    target_batch_id: '',
  });

  const [filterBatch, setFilterBatch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');

  // ── fetch ──────────────────────────────────────────────────────────────────

  const fetchItems = useCallback(async () => {
    try {
      const url = filterBatch ? `/api/feed?batch_id=${filterBatch}` : '/api/feed';
      const res = await api.get(url);
      setItems(res.data || []);
    } catch {
      showToast('Failed to load feed');
    } finally {
      setLoading(false);
    }
  }, [filterBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBatches = useCallback(async () => {
    try {
      const res = await api.get('/api/batches?per_page=200');
      setBatches(res.data.batches || res.data || []);
    } catch {
      // silently fail
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchBatches(); }, [fetchBatches]);

  // ── CRUD ───────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setForm({ type: 'video', title: '', content: '', link: '', target_batch_id: '' });
    setEditItem(null);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setForm({
      type: item.type,
      title: item.title,
      content: item.content || '',
      link: item.link || '',
      target_batch_id: item.target_batch_id || '',
    });
    setEditItem(item);
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      type: form.type,
      title: form.title.trim(),
      content: form.content.trim() || null,
      link: form.link.trim() || null,
      target_batch_id: form.target_batch_id || null,
    };
    try {
      if (editItem) {
        await api.put(`/api/feed/${editItem.id}`, payload);
        showToast('Post updated');
      } else {
        await api.post('/api/feed', payload);
        showToast('Post published');
      }
      setShowModal(false);
      setEditItem(null);
      fetchItems();
    } catch {
      showToast('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this feed post?')) return;
    try {
      await api.delete(`/api/feed/${id}`);
      showToast('Post deleted');
      fetchItems();
    } catch {
      showToast('Failed to delete post');
    }
  };

  // ── filter ─────────────────────────────────────────────────────────────────

  const filtered = items.filter((item) => {
    if (filterType && item.type !== filterType) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        item.title.toLowerCase().includes(q) ||
        (item.content || '').toLowerCase().includes(q) ||
        (item.creator_name || '').toLowerCase().includes(q)
      );
    }
    return true;
  });

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      {toast && <Toast msg={toast} onClose={() => {}} />}

      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(32,201,151,0.12)' }}>
              <svg className="w-5 h-5" style={{ color: '#20C997' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Learning Feed</h1>
              <p className="text-sm text-gray-500">{items.length} post{items.length !== 1 ? 's' : ''}</p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-xl shadow-sm transition-all hover:opacity-90 self-start sm:self-auto"
            style={{ background: 'linear-gradient(135deg,#20C997,#003479)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Post
          </button>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <svg className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search posts..."
              className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none bg-white"
          >
            <option value="">All types</option>
            {TYPES.map((t) => <option key={t} value={t}>{TYPE_META[t].label}</option>)}
          </select>
          <select
            value={filterBatch}
            onChange={(e) => setFilterBatch(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none bg-white"
          >
            <option value="">All batches</option>
            {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
          </select>
        </div>

        {/* Feed list */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5">
                <div className="skeleton h-4 w-24 mb-3" />
                <div className="skeleton h-5 w-2/3 mb-2" />
                <div className="skeleton h-4 w-full" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">No posts yet</p>
              <p className="text-xs text-gray-400 mt-1">Create your first learning feed post above</p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((item) => {
              const meta = TYPE_META[item.type] || TYPE_META.tip;
              return (
                <div key={item.id} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Badges row */}
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <TypeBadge type={item.type} />
                        {item.batch_name ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-600">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {item.batch_name}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700">
                            All students
                          </span>
                        )}
                      </div>

                      {/* Title */}
                      <h3 className="font-semibold text-gray-900 text-base leading-snug">{item.title}</h3>

                      {/* Content */}
                      {item.content && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">{item.content}</p>
                      )}

                      {/* Link */}
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-xs font-medium hover:underline"
                          style={{ color: meta.color }}
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                          Open link
                        </a>
                      )}

                      {/* Footer */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3">
                        <span className="text-xs text-gray-400">
                          by <span className="font-medium text-gray-600">{item.creator_name || 'Unknown'}</span>
                        </span>
                        <span className="text-xs text-gray-400">{formatDate(item.created_at)}</span>
                        <span className="text-xs text-gray-400">
                          <span className="font-medium text-gray-600">{item.read_count}</span> read{item.read_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => openEdit(item)}
                        className="p-1.5 text-gray-400 hover:text-[#003479] hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(item.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {showModal && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8 animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">{editItem ? 'Edit Post' : 'New Post'}</h2>
              <button onClick={() => { setShowModal(false); setEditItem(null); }} className="p-1 text-gray-400 hover:text-gray-700 rounded-lg">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSave} className="px-6 py-5 space-y-4">
              {/* Type selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {TYPES.map((t) => {
                    const meta = TYPE_META[t];
                    const selected = form.type === t;
                    return (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setForm({ ...form, type: t })}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${
                          selected ? 'border-current' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                        style={selected ? { borderColor: meta.color, color: meta.color, background: meta.bg } : {}}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={meta.icon} />
                        </svg>
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                  placeholder="What's the title of this post?"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm resize-none focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                  placeholder="Brief description or message..."
                />
              </div>

              {/* Link */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Link
                  {form.type === 'video' && <span className="text-xs text-gray-400 ml-1">(YouTube or other video URL)</span>}
                  {form.type === 'article' && <span className="text-xs text-gray-400 ml-1">(Article URL)</span>}
                  {form.type === 'job_alert' && <span className="text-xs text-gray-400 ml-1">(Job posting URL)</span>}
                </label>
                <input
                  type="url"
                  value={form.link}
                  onChange={(e) => setForm({ ...form, link: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none"
                  placeholder="https://..."
                />
              </div>

              {/* Target Batch */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                <select
                  value={form.target_batch_id}
                  onChange={(e) => setForm({ ...form, target_batch_id: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-[#20C997]/20 focus:border-[#20C997] outline-none bg-white"
                >
                  <option value="">All students (global)</option>
                  {batches.map((b) => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">Leave blank to show to all students</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditItem(null); }}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2.5 text-white text-sm font-semibold rounded-xl transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg,#20C997,#003479)' }}
                >
                  {saving ? 'Publishing…' : editItem ? 'Update Post' : 'Publish Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default TrainerFeed;
